import ErrorMessage from "components/ErrorMessage";
import OButton from "components/reusable/OButton";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";
import { validationRules } from "utils/constants";
import AuthContext from "../context/AuthContext";
import Loader from "../layout/Loader";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import { preventMaxInput } from "utils/validations";
import { isEmpty } from "lodash";
import helpers from "utils/helpers";
import Authenticator from "components/mfa/Authenticator";
import MFASetupModal from "components/mfa/MFASetupModal";
import axios from "axios";
import useToastContext from "hooks/useToastContext";
import { getDeviceId } from "utils/deviceId";

function Login() {
  const { t } = useTranslation();
  const location = useLocation();
  const [icon, setIcon] = useState(true);
  const notification = useToastContext();
  const [signupType, setSignupType] = useState("admin");

  let {
    loginUser,
    user,
    mfaChallenge,
    verifyLoginMfa,
    completeMfaLogin,
    completeMfaSetupLogin,
    finishMfaSetupLogin,
    cancelMfaLogin,
  } = useContext(AuthContext);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({ mode: "onBlur", shouldFocusError: true, defaultValues: {} });

  const [rememberMe, setRememberMe] = useState(
    localStorage.getItem("rememberMe") === "true" ? true : false,
  );
  const [currentAddress, setCurrentAddress] = useState("");
  const loginGpsRef = useRef({ latitude: null, longitude: null, address: "" });

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
        loginGpsRef.current.latitude = latitude;
        loginGpsRef.current.longitude = longitude;
        try {
          const response = await axios.get(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyCyfi1GcGsUisGcHoaRNxTTq2VCoEXb1h8`,
          );
          if (response.data.results.length > 0) {
            const address = (
              response.data.results[0] || response.data.results[8]
            ).formatted_address;
            setCurrentAddress(address);
            loginGpsRef.current.address = address;
          }
        } catch (error) {
          console.error("Error fetching address:", error);
        }
      });
    }
  };

  const waitForLoginGps = () =>
    new Promise((resolve) => {
      if (loginGpsRef.current.latitude != null && loginGpsRef.current.address) {
        resolve(loginGpsRef.current);
        return;
      }
      getCurrentLocation();
      const started = Date.now();
      const timer = setInterval(() => {
        if (
          (loginGpsRef.current.latitude != null &&
            loginGpsRef.current.address) ||
          Date.now() - started > 6000
        ) {
          clearInterval(timer);
          resolve(loginGpsRef.current);
        }
      }, 200);
    });

  const handleRememberMe = (e) => {
    localStorage.setItem("rememberMe", e.target.checked);
    setRememberMe(e.target.checked);
  };

  useEffect(() => {
    if (rememberMe) {
      reset({
        email: localStorage.getItem("email"),
        password: localStorage.getItem("password"),
      });
    }
  }, []);

  const onSubmit = async (data) => {
    data.email = data?.email?.trim();
    data.password = data?.password?.trim();
    if (rememberMe) {
      localStorage.setItem("email", data.email);
      localStorage.setItem("password", data.password);
    } else {
      localStorage.removeItem("email");
      localStorage.removeItem("password");
    }
    const gps = await waitForLoginGps();
    let loginIp = sessionStorage.getItem("loginIp") || "";
    if (!loginIp) {
      try {
        const ipRes = await fetch("https://api.ipify.org?format=json");
        loginIp = String((await ipRes.json())?.ip || "").trim();
        if (loginIp) sessionStorage.setItem("loginIp", loginIp);
      } catch (err) {
        loginIp = "";
      }
    }
    const loginLocation = {
      latitude: gps.latitude,
      longitude: gps.longitude,
      loginAddress: gps.address || currentAddress,
      loginIp,
    };
    sessionStorage.setItem("loginGps", JSON.stringify(loginLocation));

    // Redirect + temporary-password popup are handled inside
    // AuthContext.finalizeLogin(), not in this component.
    loginUser({
      ...data,
      type: signupType,
      ...loginLocation,
      deviceId: getDeviceId(),
    });
  };

  useEffect(() => {
    getCurrentLocation();
  }, []);

  useEffect(() => {
    const msg = sessionStorage.getItem("ipSessionMessage");
    if (msg) {
      sessionStorage.removeItem("ipSessionMessage");
      notification.error(msg);
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(user)) {
      setSignupType(user);
    }
  }, [user]);

  useEffect(() => {
    if (mfaChallenge) {
      cancelMfaLogin();
    }
  }, [location.pathname]);

  function changeIcon() {
    setIcon(!icon);
  }

  let forgotPasswordLink;

  if (location.pathname === "/hr-login") {
    forgotPasswordLink = "/forgot-password-hr";
  } else if (location.pathname === "/accountant-login") {
    forgotPasswordLink = "/forgot-password-accountant";
  } else {
    forgotPasswordLink = "/forgot-password";
  }

  const codeValue = helpers.ternaryCondition(
    watch("email"),
    watch("email"),
    "",
  );

  return (
    <div className="bg-gradient-to-r from-gradblue to-LightBlue h-full">
      <Loader />
      <div className="p-4">
        <div className="login-form bg-white max-w-lg m-auto mt-10 sm:mt-16 md:mt-28 rounded-[20px]">
          <form
            className="sm:py-12 sm:px-11 py-8 px-7"
            onSubmit={handleSubmit(onSubmit)}
            method="post"
          >
            <img src="./images/empivo.png" alt="" className="m-auto py-2" />
            <h1 className="text-center text-[40px] font-bold">
              {t("LOGIN_LETS_START")}!
            </h1>
            <h2 className="text-center text-lg text-[#A5A5A5] sm:mb-12 mb-6">
              {t("LOGIN_ONLY_FEW_MINUTES")}
            </h2>
            <div className="relative z-0 mb-6 w-full group">
              <input
                type="text"
                id="email"
                className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0  peer"
                placeholder=" "
                value={codeValue.toLowerCase()}
                onInput={(e) => preventMaxInput(e, 50)}
                {...register("email", {
                  required: "Please enter email ID.",
                  pattern: {
                    value: validationRules.email,
                    message:
                      "Please enter valid email ID as: example@domain.com.",
                  },
                })}
              />

              <label
                htmlFor="email"
                className="peer-focus:font-normal absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
              >
                {t("O_EMAIL_ID")}
                <span className="text-red-500">*</span>
              </label>
              <ErrorMessage message={errors?.email?.message} />
            </div>
            <div className="relative z-0 mb-6 w-full group">
              <input
                type={helpers?.ternaryCondition(icon, "password", "text")}
                name="password"
                id="password"
                maxLength={40}
                onInput={(e) => preventMaxInput(e, 40)}
                className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0  peer"
                placeholder=" "
                autoComplete="new-password"
                {...register("password", {
                  required: "Please enter password.",
                  pattern: {
                    value: validationRules.password,
                    message:
                      "Password must contain lowercase,uppercase characters, numbers, special character and must be 8 character long.",
                  },
                })}
              />
              <label
                htmlFor="password"
                className="peer-focus:font-normal absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
              >
                {t("O_PASSWORD")}
                <span className="text-red-500">*</span>
              </label>
              {icon ? (
                <span
                  className="password_view cursor-pointer absolute top-[18px] right-[20px]"
                  onClick={() => changeIcon()}
                >
                  <AiFillEyeInvisible />
                </span>
              ) : (
                <span
                  className="password_view absolute top-[18px] right-[20px]"
                  onClick={() => changeIcon()}
                >
                  <AiFillEye />
                </span>
              )}
              <ErrorMessage message={errors?.password?.message} />
            </div>
            <div className="flex items-start mb-8">
              <div className="flex items-center h-5">
                <div>
                  <input
                    id="remember"
                    type="checkbox"
                    checked={helpers?.ternaryCondition(rememberMe, true, false)}
                    className="w-4 h-4 bg-gray-50 rounded border border-[#DFDFDF]  focus:ring-3 focus:ring-[#DFDFDF] dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-[#DFDFDF] dark:ring-offset-gray-800"
                    onChange={(e) => handleRememberMe(e)}
                  />
                </div>
                <label
                  htmlFor="remember"
                  className="ml-2 text-sm text-black dark:text-gray-300"
                >
                  {t("LOGIN_REMEMBER_ME")}
                </label>
              </div>
              <Link
                to={forgotPasswordLink}
                className="ml-auto text-[#6236FF] hover:text-[#9D36FF] hover:underline text-sm font-medium"
              >
                {t("LOGIN_FORGOT_PASSWORD")}?
              </Link>
            </div>
            <div className="text-center mt-8">
              <OButton label={<>{t("LOGIN_LOGIN")}</>} type="submit" />
            </div>
          </form>
        </div>
      </div>

      <Authenticator
        isOpen={!!mfaChallenge && !mfaChallenge.setup}
        onClose={cancelMfaLogin}
        onVerify={verifyLoginMfa}
        onSuccess={completeMfaLogin}
        allowRecoveryCode
        title={t("MFA_LOGIN_TITLE") || "Two-factor verification"}
        description={
          t("MFA_LOGIN_DESC") ||
          "Enter the 6-digit code from your authenticator app to finish signing in."
        }
      />
      <MFASetupModal
        isOpen={!!mfaChallenge?.setup}
        onClose={cancelMfaLogin}
        onEnabled={finishMfaSetupLogin}
        loginSetup={
          mfaChallenge?.setup
            ? {
                qrCodeDataUrl: mfaChallenge.qrCodeDataUrl,
                manualEntryKey: mfaChallenge.manualEntryKey,
              }
            : null
        }
        onLoginConfirm={completeMfaSetupLogin}
      />
    </div>
  );
}

export default Login;





// import ErrorMessage from "components/ErrorMessage";
// import OButton from "components/reusable/OButton";
// import React, { useContext, useEffect, useRef, useState } from "react";
// import { useForm } from "react-hook-form";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { validationRules } from "utils/constants";
// import AuthContext from "../context/AuthContext";
// import Loader from "../layout/Loader";
// import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
// import { useTranslation } from "react-i18next";
// import { preventMaxInput } from "utils/validations";
// import { isEmpty } from "lodash";
// import helpers from "utils/helpers";
// import Authenticator from "components/mfa/Authenticator"; // MFA: reusable code-entry modal
// import MFASetupModal from "components/mfa/MFASetupModal";
// import axios from "axios";
// import useToastContext from "hooks/useToastContext";
// import { getDeviceId } from "utils/deviceId";

// function Login() {
//   const { t } = useTranslation();
//   const location = useLocation();
//   const [icon, setIcon] = useState(true);
//   const notification = useToastContext();
//   const [signupType, setSignupType] = useState("admin");
//   const navigate = useNavigate();
//   let {
//     loginUser,
//     user,
//     // MFA: login-time step-up state + actions from AuthContext
//     mfaChallenge,
//     verifyLoginMfa,
//     completeMfaLogin,
//     completeMfaSetupLogin,
//     finishMfaSetupLogin,
//     cancelMfaLogin,
//   } = useContext(AuthContext);
//   const {
//     register,
//     handleSubmit,
//     reset,
//     watch,
//     formState: { errors },
//   } = useForm({ mode: "onBlur", shouldFocusError: true, defaultValues: {} });
//   const [rememberMe, setRememberMe] = useState(
//     localStorage.getItem("rememberMe") === "true" ? true : false,
//   );
//   const [currentAddress, setCurrentAddress] = useState("");
//   const loginGpsRef = useRef({ latitude: null, longitude: null, address: "" });

//   const getCurrentLocation = () => {
//     if (navigator.geolocation) {
//       navigator.geolocation.getCurrentPosition(async (position) => {
//         const { latitude, longitude } = position.coords;
//         loginGpsRef.current.latitude = latitude;
//         loginGpsRef.current.longitude = longitude;
//         try {
//           const response = await axios.get(
//             `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyCyfi1GcGsUisGcHoaRNxTTq2VCoEXb1h8`,
//           );
//           if (response.data.results.length > 0) {
//             // PREVIOUS: results[8] is city only (Noida, Uttar Pradesh 201309), not current pin
//             // const address = (
//             //   response.data.results[8] || response.data.results[0]
//             // ).formatted_address;
//             const address = (
//               response.data.results[0] || response.data.results[8]
//             ).formatted_address;
//             setCurrentAddress(address);
//             loginGpsRef.current.address = address;
//           } else {
//             console.error("No address found for the given coordinates.");
//           }
//         } catch (error) {
//           console.error("Error fetching address:", error);
//         }
//       });
//     } else {
//       console.error("Geolocation is not supported by your browser.");
//     }
//   };

//   const waitForLoginGps = () =>
//     new Promise((resolve) => {
//       if (loginGpsRef.current.latitude != null && loginGpsRef.current.address) {
//         resolve(loginGpsRef.current);
//         return;
//       }
//       getCurrentLocation();
//       const started = Date.now();
//       const timer = setInterval(() => {
//         if (
//           (loginGpsRef.current.latitude != null && loginGpsRef.current.address) ||
//           Date.now() - started > 6000
//         ) {
//           clearInterval(timer);
//           resolve(loginGpsRef.current);
//         }
//       }, 200);
//     });

//   const handleRememberMe = (e) => {
//     localStorage.setItem("rememberMe", e.target.checked);
//     setRememberMe(e.target.checked);
//   };

//   useEffect(() => {
//     if (rememberMe) {
//       reset({
//         email: localStorage.getItem("email"),
//         password: localStorage.getItem("password"),
//       });
//     }
//   }, []);

//   const onSubmit = async (data) => {
//     data.email = data?.email?.trim();
//     data.password = data?.password?.trim();
//     if (rememberMe) {
//       localStorage.setItem("email", data.email);
//       localStorage.setItem("password", data.password);
//     } else {
//       localStorage.removeItem("email");
//       localStorage.removeItem("password");
//     }
//     const gps = await waitForLoginGps();
//     let loginIp = sessionStorage.getItem("loginIp") || "";
//     if (!loginIp) {
//       try {
//         const ipRes = await fetch("https://api.ipify.org?format=json");
//         loginIp = String((await ipRes.json())?.ip || "").trim();
//         if (loginIp) sessionStorage.setItem("loginIp", loginIp);
//       } catch (err) {
//         loginIp = "";
//       }
//     }
//     const loginLocation = {
//       latitude: gps.latitude,
//       longitude: gps.longitude,
//       loginAddress: gps.address || currentAddress,
//       loginIp,
//     };
//     sessionStorage.setItem("loginGps", JSON.stringify(loginLocation));
//     // loginUser({ ...data, type: signupType, ...loginLocation });
//     loginUser({ ...data, type: signupType, ...loginLocation, deviceId: getDeviceId() });
//   };

//   useEffect(() => {
//     getCurrentLocation();
//   }, []);

//   useEffect(() => {
//     const msg = sessionStorage.getItem("ipSessionMessage");
//     if (msg) {
//       sessionStorage.removeItem("ipSessionMessage");
//       notification.error(msg);
//     }
//   }, []);

//   useEffect(() => {
//     if (user !== null && localStorage.getItem("token")) {
//       navigate("/dashboard");
//     }
//   }, [user]);

//   useEffect(() => {
//     if (!isEmpty(user)) {
//       setSignupType(user);
//     }
//   }, [user]);

//   useEffect(() => {
//     if (mfaChallenge) {
//       cancelMfaLogin();
//     }
//   }, [location.pathname]);

//   function changeIcon() {
//     setIcon(!icon);
//   }

//   let forgotPasswordLink;

//   if (location.pathname === "/hr-login") {
//     forgotPasswordLink = "/forgot-password-hr";
//   } else if (location.pathname === "/accountant-login") {
//     forgotPasswordLink = "/forgot-password-accountant";
//   } else {
//     forgotPasswordLink = "/forgot-password";
//   }

//   const codeValue = helpers.ternaryCondition(
//     watch("email"),
//     watch("email"),
//     "",
//   );

//   return (
//     <div className="bg-gradient-to-r from-gradblue to-LightBlue h-full">
//       <Loader />
//       <div className="p-4">
//         <div className="login-form bg-white max-w-lg m-auto mt-10 sm:mt-16 md:mt-28 rounded-[20px]">
//           <form
//             className="sm:py-12 sm:px-11 py-8 px-7"
//             onSubmit={handleSubmit(onSubmit)}
//             method="post"
//           >
//             <img src="./images/empivo.png" alt="" className="m-auto py-2" />
//             <h1 className="text-center text-[40px] font-bold">
//               {t("LOGIN_LETS_START")}!
//             </h1>
//             <h2 className="text-center text-lg text-[#A5A5A5] sm:mb-12 mb-6">
//               {t("LOGIN_ONLY_FEW_MINUTES")}
//             </h2>
//             <div className="relative z-0 mb-6 w-full group">
//               <input
//                 type="text"
//                 id="email"
//                 className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0  peer"
//                 placeholder=" "
//                 value={codeValue.toLowerCase()}
//                 onInput={(e) => preventMaxInput(e, 50)}
//                 {...register("email", {
//                   required: "Please enter email ID.",
//                   pattern: {
//                     value: validationRules.email,
//                     message:
//                       "Please enter valid email ID as: example@domain.com.",
//                   },
//                 })}
//               />

//               <label
//                 htmlFor="email"
//                 className="peer-focus:font-normal absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
//               >
//                 {t("O_EMAIL_ID")}
//                 <span className="text-red-500">*</span>
//               </label>
//               <ErrorMessage message={errors?.email?.message} />
//             </div>
//             <div className="relative z-0 mb-6 w-full group">
//               <input
//                 type={helpers?.ternaryCondition(icon, "password", "text")}
//                 name="password"
//                 id="password"
//                 maxLength={40}
//                 onInput={(e) => preventMaxInput(e, 40)}
//                 className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0  peer"
//                 placeholder=" "
//                 autoComplete="new-password"
//                 {...register("password", {
//                   required: "Please enter password.",
//                   pattern: {
//                     value: validationRules.password,
//                     message:
//                       "Password must contain lowercase,uppercase characters, numbers, special character and must be 8 character long.",
//                   },
//                 })}
//               />
//               <label
//                 htmlFor="password"
//                 className="peer-focus:font-normal absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 origin-[2] peer-focus:left-0 peer-focus:text-[#A5A5A5] peer-focus:text-lg peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8"
//               >
//                 {t("O_PASSWORD")}
//                 <span className="text-red-500">*</span>
//               </label>
//               {icon ? (
//                 <span
//                   className="password_view cursor-pointer absolute top-[18px] right-[20px]"
//                   onClick={() => changeIcon()}
//                 >
//                   <AiFillEyeInvisible />
//                 </span>
//               ) : (
//                 <span
//                   className="password_view absolute top-[18px] right-[20px]"
//                   onClick={() => changeIcon()}
//                 >
//                   <AiFillEye />
//                 </span>
//               )}
//               <ErrorMessage message={errors?.password?.message} />
//             </div>
//             <div className="flex items-start mb-8">
//               <div className="flex items-center h-5">
//                 <div>
//                   <input
//                     id="remember"
//                     type="checkbox"
//                     checked={helpers?.ternaryCondition(rememberMe, true, false)}
//                     className="w-4 h-4 bg-gray-50 rounded border border-[#DFDFDF]  focus:ring-3 focus:ring-[#DFDFDF] dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-[#DFDFDF] dark:ring-offset-gray-800"
//                     onChange={(e) => handleRememberMe(e)}
//                   />
//                 </div>
//                 <label
//                   htmlFor="remember"
//                   className="ml-2 text-sm text-black dark:text-gray-300"
//                 >
//                   {t("LOGIN_REMEMBER_ME")}
//                 </label>
//               </div>
//               <Link
//                 to={forgotPasswordLink}
//                 className="ml-auto text-[#6236FF] hover:text-[#9D36FF] hover:underline text-sm font-medium"
//               >
//                 {t("LOGIN_FORGOT_PASSWORD")}?
//               </Link>
//             </div>
//             <div className="text-center mt-8">
//               <OButton label={<>{t("LOGIN_LOGIN")}</>} type="submit" />
//             </div>
//           </form>
//         </div>
//       </div>

//       {/* MFA: shown when the backend responds with mfaRequired instead of a token */}
//       <Authenticator
//         isOpen={!!mfaChallenge && !mfaChallenge.setup}
//         onClose={cancelMfaLogin}
//         onVerify={verifyLoginMfa}
//         onSuccess={completeMfaLogin}
//         allowRecoveryCode
//         title={t("MFA_LOGIN_TITLE") || "Two-factor verification"}
//         description={
//           t("MFA_LOGIN_DESC") ||
//           "Enter the 6-digit code from your authenticator app to finish signing in."
//         }
//       />
//       <MFASetupModal
//         isOpen={!!mfaChallenge?.setup}
//         onClose={cancelMfaLogin}
//         onEnabled={finishMfaSetupLogin}
//         loginSetup={
//           mfaChallenge?.setup
//             ? {
//                 qrCodeDataUrl: mfaChallenge.qrCodeDataUrl,
//                 manualEntryKey: mfaChallenge.manualEntryKey,
//               }
//             : null
//         }
//         onLoginConfirm={completeMfaSetupLogin}
//       />
//     </div>
//   );
// }

// export default Login;